// src/portal/ShortlistedModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, Info, Loader2, AlertCircle, AlertTriangle, SlidersHorizontal, Check, ChevronDown, Plus, Phone, Mail, MoreHorizontal } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { STORAGE_KEYS } from "../../utils/constants";
import {
  getShortlistedProducts,
  deleteShortlistedProductByName,
} from "../../api/borrowerPortal/shortlistedModal.js";
import { isAuthenticated, getBorrowerEmail } from "../../utils/auth.js";
import { getBorrowerProfile } from "../../api/borrowerPortal/borrowerProfile.js";
import { submitCallbackRequest } from "../../api/reviews.js";
import PhoneField from "../../components/ui/PhoneField.jsx";
import { isValidPhone } from "../../utils/validatePhone.js";
import { listApplications } from "../../api/borrowerPortal/applications.js";
import {
  rememberSourcingCategory,
  resolveShortlistCategory,
  sourcingPathForCategory,
  shortlistPathForCategory,
  shortlistTabForCategory,
} from "../../utils/sourcingCategory.js";
import { formatInvoiceFinanceProductTitle, resolveInvoiceFinanceFacilityType } from "../../api/invoiceFinanceSourcing.js";
import TypeBadge from "../../components/ui/TypeBadge.jsx";
import PropertyTypeChips from "../../components/ui/PropertyTypeChips.jsx";
import { resolveBorrowerProductCategory } from "../../utils/complexBtlProduct.js";

/* ---------------- Storage Keys ---------------- */
const LS_SHORTLIST = STORAGE_KEYS.SHORTLIST;
const LS_PENDING = STORAGE_KEYS.SHORTLIST_PENDING;
const INTENT_KEY = "ds_app_intent"; // MUST match NewApplication.jsx

/* ---------------- Helpers ---------------- */

/** Format ICR as exact % (e.g. 5.5 → "5.5%", 6 → "6%") — mirrors sourcing page. */
function formatIcrPercent(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return "—";
  const s = x.toFixed(2).replace(/\.?0+$/, "");
  return `${s}%`;
}

function formatErcPercent(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return "0%";
  const s = x.toFixed(8).replace(/\.?0+$/, "");
  return `${s}%`;
}

/* Helper: Get all possible identifiers for a product for matching */
const getProductIdentifiers = (product) => {
  if (!product) return [];

  const ids = [
    product.mortgage_product,
    product.product_id,
    product.id,
    product.name,
    product._raw?.mortgage_product,
    product._raw?.product_id,
    product._raw?.name,
    product._raw?.product,
  ];

  return ids
    .filter(Boolean)
    .map(id => String(id).trim().toLowerCase())
    .filter(id => id && id !== 'undefined' && id !== 'null');
};

const safeReadArray = (key) => {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const safeWriteArray = (key, arr) => {
  try {
    localStorage.setItem(key, JSON.stringify(Array.isArray(arr) ? arr : []));
  } catch { }
};

const firstFinite = (...vals) => {
  for (const v of vals) {
    if (v == null) continue;
    if (typeof v === "string") {
      const s = v.trim();
      const m = s.match(/-?\d+(?:\.\d+)?/);
      if (m) {
        const n = Number(m[0]);
        if (Number.isFinite(n)) return n;
      }
      continue;
    }
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
};

/** Like firstFinite, but skips 0 / negative — IF often stores term_months as 0. */
const firstPositiveFinite = (...vals) => {
  for (const v of vals) {
    if (v == null || v === "") continue;
    if (typeof v === "string") {
      const s = v.trim();
      const m = s.match(/-?\d+(?:\.\d+)?/);
      if (m) {
        const n = Number(m[0]);
        if (Number.isFinite(n) && n > 0) return n;
      }
      continue;
    }
    const n = Number(v);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
};

const safeNum = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

// Helper: resolve Arrangement Fee using same logic as Sourcing
// eslint-disable-next-line no-unused-vars
function resolveArrangementFeeAmount(p, mortgageAmount = 0) {
  const typeRaw =
    p?.product_fee_type ||
    p?.arrangement_fee_type ||
    p?.fee_type ||
    p?.product_fee_type_selector ||
    "";
  const type = String(typeRaw || "").trim().toLowerCase();

  const feeAmount = firstFinite(
    p?.product_fee_amount,
    p?.arrangement_fee_amount,
    p?.arrangement_fee,
    p?.product_fee
  );
  const feePercent = firstFinite(
    p?.product_fee_percent,
    p?.arrangement_fee_percent,
    p?.fee_percent
  );
  const minFee = firstFinite(
    p?.min_arrangement_fee,
    p?.min_fee,
    p?.arrangement_min_fee,
    p?.product_min_fee
  );

  const mort = safeNum(mortgageAmount);
  // Calculation helpers
  const pctFee = () => mort * (safeNum(feePercent) / 100);
  const amtFee = () => safeNum(feeAmount);
  const minOnly = () => safeNum(minFee);

  const isPercent = type.includes("percent") || type.includes("percentage") || type === "%";
  const isMinimum = type.includes("minimum") || type.includes("min");
  const isAmount = type.includes("£") || type.includes("amount") || type.includes("fixed") || type === "gbp";

  let fee = 0;
  if (isPercent) {
    fee = pctFee();
    if (Number.isFinite(Number(minFee)) && Number(minFee) > 0) fee = Math.max(fee, minOnly());
    return Math.max(0, safeNum(fee));
  }
  if (isMinimum) {
    if (Number.isFinite(Number(feePercent)) && Number(feePercent) > 0) fee = Math.max(pctFee(), minOnly());
    else if (Number.isFinite(Number(feeAmount)) && Number(feeAmount) > 0) fee = Math.max(amtFee(), minOnly());
    else fee = minOnly();
    return Math.max(0, safeNum(fee));
  }
  if (isAmount) {
    fee = amtFee();
    return Math.max(0, safeNum(fee));
  }

  // Fallback
  if (Number.isFinite(Number(feeAmount)) && Number(feeAmount) > 0) fee = amtFee();
  else if (Number.isFinite(Number(feePercent)) && Number(feePercent) > 0) fee = pctFee();
  else if (Number.isFinite(Number(minFee)) && Number(minFee) > 0) fee = minOnly();

  return Math.max(0, safeNum(fee));
}

const normalizeProductType = (product) => resolveBorrowerProductCategory(product);

const runSafeNum = (v) => Number.isFinite(Number(v)) ? Number(v) : null;

// Helper to parse percentage string (e.g., "3.920%" -> 3.92)
const parsePercentage = (val) => {
  if (typeof val === "number") return val;
  if (typeof val === "string") {
    const cleaned = val.replace(/%/g, "").trim();
    const num = Number(cleaned);
    return Number.isFinite(num) ? num : null;
  }
  return null;
};

// Helper to parse term string (e.g., "25 yrs" -> 25)
const parseTermYears = (val) => {
  if (typeof val === "number") return val;
  if (typeof val === "string") {
    const match = val.match(/(\d+(?:\.\d+)?)/);
    if (match) {
      const num = Number(match[1]);
      return Number.isFinite(num) ? num : null;
    }
  }
  return null;
};

const normalizeFilterToken = (value) => String(value ?? "").trim().toLowerCase();
const firstNonEmpty = (...values) => values.find((value) => String(value ?? "").trim() !== "");

/** Review limit = facility + 10%, rounded up to nearest £10k (same as IF sourcing). */
const calcIfReviewLimit = (facilityAmount) => {
  const facility = Number(facilityAmount);
  if (!Number.isFinite(facility) || facility <= 0) return null;
  return Math.ceil((facility * 1.1) / 10000) * 10000;
};

/**
 * IF service fee % from shortlist API.
 * Prefer service_fee_percent (e.g. 0.97). Avoid simple_service_fee when it looks
 * like a wrongly scaled value (e.g. 97 for 0.97%).
 */
const resolveIfServiceFeePercent = (it = {}) => {
  const fromService = firstFinite(it.service_fee_percent, it.serviceFeePercent);
  if (fromService != null) return fromService;
  const fromAnnual = firstFinite(it.annualCostPct, it.annual_cost_pct);
  if (fromAnnual != null) return fromAnnual;
  const simple = firstFinite(it.simple_service_fee, it.simpleServiceFee);
  if (simple == null) return null;
  // Backend sometimes stores 0.97% as 97 on simple_service_fee
  if (simple > 20) return Math.round((simple / 100) * 1e8) / 1e8;
  return simple;
};

/** Same base rate used on IF sourcing discount-fee calc. */
const IF_BASE_RATE_PCT = 3.75;
const IF_DEFAULT_UTILISATION_PCT = 60;

/**
 * Estimate IF full-details fee costs when API omits them.
 * Mirrors InvoiceFinanceSourcing calculateAdjustedProductCosts.
 */
const estimateIfDetailFeeCosts = (it = {}) => {
  const facility = firstPositiveFinite(
    it.facility_required,
    it.facilityRequired,
    it.headlineAmount,
    it.loan_amount,
    it.mortgageAmount
  ) ?? 0;
  const netAdvance =
    firstPositiveFinite(it.netAdvance, it.net_advance) ?? facility;
  const turnover = firstPositiveFinite(it.projected_turnover, it.projectedTurnover) ?? 0;
  const utilisationPct =
    firstPositiveFinite(it.utilisationPct, it.utilisation_pct) ?? IF_DEFAULT_UTILISATION_PCT;
  const utilisation = Math.min(Math.max(utilisationPct, 0), 100) / 100;
  const discountPct =
    firstFinite(
      it.discount_rate_percent,
      it.discountRatePercent,
      it.discountRatePct,
      it.interest_rate
    ) ?? 0;
  const serviceFeePct = resolveIfServiceFeePercent(it) ?? 0;
  const minServiceFee =
    firstFinite(it.min_service_fee, it.minServiceFee, it.miniServiceFeePct) ?? 0;
  const termYears = 1;

  const discountRate = (Number(discountPct) + IF_BASE_RATE_PCT) / 100;
  const discountFeeAmount = Math.round(netAdvance * discountRate * utilisation * termYears);

  const serviceFeeRate = Number(serviceFeePct) / 100;
  const rawAnnualService = turnover > 0 ? turnover * serviceFeeRate : 0;
  // min_service_fee from API is £; values under 100 may be % of facility
  const minFeeAmount =
    minServiceFee > 0 && minServiceFee < 100 && facility > 0
      ? Math.round((facility * minServiceFee) / 100)
      : Math.round(minServiceFee);
  const serviceFeeAmount = Math.round(Math.max(rawAnnualService, minFeeAmount) * termYears);

  return {
    discountFeeAmount,
    serviceFeeAmount,
    utilisationPct: Math.round(utilisation * 100),
    utilisedFacility: Math.round(facility * utilisation),
  };
};

const isVisibleShortlistedProduct = (item) => {
  const status = String(
    item?.product_status || item?.status || (item?._isScheduled ? "scheduled" : "")
  ).toLowerCase();
  if (status === "deactivated") return false;
  if (!status) return true;
  return status === "active" || status === "live" || status === "pending";
};

const normaliseItem = (it) => {
  if (!it) return null;
  // Pass full product object to normalizeProductType so it can check category fields
  const normType = normalizeProductType(it);

  // Backend field mappings
  const productId = it.mortgage_product || it.product_id || it.id || it.name;
  const loanAmount = safeNum(it.loan_amount || it.mortgageAmount || it.mortgage_amount || 0);

  // Parse interest rate (backend returns "3.920%" as string)
  const interestRateStr = it.interest_rate || it.rate || "";
  const interestRate = parsePercentage(interestRateStr) ||
    (typeof it.interest_rate === "number" ? it.interest_rate : null) ||
    (typeof it.rate === "number" ? it.rate : null);

  // Parse LTV (backend returns "60%" as string)
  const ltvStr = it.ltv || "";
  const ltv = parsePercentage(ltvStr) ||
    (typeof it.ltv === "number" ? it.ltv : null);

  // Parse mortgage term (backend returns "25 yrs" as string)
  // Mortgage term is the loan duration (e.g., 25 years), NOT product term (rate period)
  const mortgageTermStr = it.mortgage_term || "";
  const mortgageTermYears = parseTermYears(mortgageTermStr) ||
    firstFinite(it.mortgage_term_years, it.term_years); // Use mortgage_term_years, NOT productTerm

  // Product term is separate - the rate period (e.g., 0.5, 2, 5 years)
  // CRITICAL: Backend overwrites product_termsyears with original Product value
  // So we prioritize product_term_years and product_term
  const productTermYears = firstFinite(
    it.product_term_years,
    it.product_term,
    it._raw?.product_term_years,
    it._raw?.product_term,
    it.productTerm,
    it.product_termsyears,
    it._raw?.product_termsyears
  );
  const productTermRaw = firstNonEmpty(
    it.shortlisted_product_term,
    it.product_term_years,
    it.product_term,
    it._raw?.product_term_years,
    it._raw?.product_term,
    it._raw?.shortlisted_product_term,
    it.productTerm,
    it.product_termsyears,
    it._raw?.product_termsyears
  );

  if (productTermYears != null) {
    console.log("📊 Product term resolved:", {
      productId: it.mortgage_product || it.id,
      productTermYears,
      from: it.product_term_years ? "product_term_years" :
        it.product_term ? "product_term" :
          it.product_termsyears ? "product_termsyears (overwritten by backend)" : "unknown"
    });
  }

  let termMonths = null;
  if (normType === "IF") {
    // Prefer product contract_term_months only — never AB term or mortgage_term
    const raw = it._raw || {};
    termMonths = firstPositiveFinite(
      it.contract_term_months,
      raw.contract_term_months,
      it.termMonths,
      it.term_months,
      raw.termMonths,
      raw.term_months
    );
  } else if (mortgageTermYears != null && mortgageTermYears > 0) {
    termMonths = Math.round(mortgageTermYears * 12);
  } else {
    termMonths = it.termMonths ?? it.term_months;
  }

  // Arrangement fee: use only values from sourcing (arrangement_fee / fees.arrangement / product_fee_amount for % type) — do not fall back to total_fees
  const feeTypeRaw = it.arrangement_fee_type ?? it.product_fee_type ?? it.fee_type ?? "";
  const isPctType = String(feeTypeRaw).toLowerCase().includes("percent");
  let arrangementFee = safeNum(it.arrangement_fee ?? it.fees?.arrangement ?? it.fee);
  if (arrangementFee === 0 && (it.product_fee_amount != null && it.product_fee_amount !== "")) {
    const amt = safeNum(it.product_fee_amount);
    if (!isPctType && amt > 100) arrangementFee = amt; // pound amount from sourcing
    else if (isPctType && amt >= 0 && amt <= 100) arrangementFee = amt; // percent value for display when type is Percentage
  }

  // Extract rate type (Fixed, Variable, Discounted) - check if backend provides it
  // If not, we might need to infer from product data or leave as null
  const rateType = it.rate_type || it.rateType || null;

  // Parse ERC data: ensure years 1-5 with 0% for any missing year (sourcing sends 0 for no charge)
  const ercByYear = {};
  if (Array.isArray(it.shortlisted_erc) && it.shortlisted_erc.length > 0) {
    it.shortlisted_erc.forEach(erc => {
      const y = erc.year ?? (typeof erc.year_no === "string" ? parseInt(erc.year_no.replace("Year ", "").trim(), 10) : null);
      if (y >= 1 && y <= 5) {
        const pct = erc.charge_percent ?? erc.charge;
        ercByYear[y] = pct != null && Number.isFinite(Number(pct)) ? Number(pct) : 0;
      }
    });
  }
  const ercs = [1, 2, 3, 4, 5].map(y => ({
    year: String(y),
    val: formatErcPercent(ercByYear[y] ?? 0)
  }));

  // ✅ USE BACKEND VALUES DIRECTLY - NO LOCALSTORAGE FALLBACK
  const finalLoanAmount = loanAmount;
  const finalMonthlyCost = safeNum(it.monthly_payment || it.monthlyCost || it.repayment || 0);
  const finalTotalCost = safeNum(it.cost_over_product_term || it.total_cost || it.totalCost || 0);
  const finalArrangementFee = arrangementFee;

  // IF: estimate fee costs / utilisation when API omits them (match sourcing calc)
  const ifFeeEstimates = normType === "IF" ? estimateIfDetailFeeCosts({
    ...it,
    termMonths,
    contract_term_months: termMonths ?? it.contract_term_months,
  }) : null;

  // Mirror sourcing Full details AB block: fee £ = principal × arrangement_fee_term (%)
  const abCalc = (() => {
    const existing = it.abCalc && typeof it.abCalc === "object" ? it.abCalc : null;
    const abPrincipal = firstPositiveFinite(
      existing?.abPrincipal,
      it.ab_principal,
      it.additional_borrowing_amount,
      it.additionalLoanAmount
    );
    if (!abPrincipal) return existing;

    const arrangementFeePct = firstFinite(
      existing?.arrangementFeePct,
      it.arrangement_fee_term,
      it.arrangementFeeTerm,
      it._raw?.arrangement_fee_term,
      it.sourceProduct?.arrangement_fee_term
    );
    let abArrangementFee = null;
    if (arrangementFeePct != null && Number.isFinite(arrangementFeePct)) {
      abArrangementFee = Math.round(abPrincipal * (arrangementFeePct / 100));
    } else {
      abArrangementFee = firstFinite(existing?.arrangementFee, it.ab_arrangement_fee);
    }

    return {
      ...(existing || {}),
      abPrincipal,
      interestRatePct: firstFinite(
        existing?.interestRatePct,
        it.additional_borrowing_interest_rate,
        it.interest_rate
      ),
      first12MonthsInterest: firstFinite(
        existing?.first12MonthsInterest,
        it.first_12_month_interest
      ),
      arrangementFeePct: arrangementFeePct ?? existing?.arrangementFeePct ?? null,
      arrangementFee: abArrangementFee ?? null,
      termMonths: firstPositiveFinite(
        existing?.termMonths,
        it.ab_term_months,
        it.contract_term_months,
        it.termMonths
      ),
      monthlyRepayment: firstFinite(
        existing?.monthlyRepayment,
        it.monthly_repayments,
        it.monthly_payment,
        it.monthlyCost
      ),
      totalRepayable: firstFinite(existing?.totalRepayable, it.total_repayable),
      costOverTerm: firstFinite(
        existing?.costOverTerm,
        it.ab_cost_over_product_term,
        it.ab_cost_over_term
      ),
      repaymentType:
        existing?.repaymentType || it.io_or_capital_repayment || "Capital & Interest",
    };
  })();

  return {
    ...it, // Keep raw fields! This includes the original 'name' field from backend
    id: productId || `${it.lender_id || it.lenderId || "lender"}-${Math.random()}`,
    product_id: productId,
    productType: normType, // Category: BTL, IF, ASSET, etc.
    lenderId: it.lender_id ?? it.lenderId ?? "unknown",
    lenderName: it.lender_name ?? it.lenderName ?? it.lender ?? "Unknown Lender",
    // Preserve the original 'name' field from backend (document name like "9c7kpsajg8") for deletion
    // Store it in both 'name' (for API) and 'shortlisted_doc_name' (for clarity)
    shortlisted_doc_name: it.name, // Original document name from backend (needed for deletion API)
    name: (() => {
      if (normType === "IF") {
        return formatInvoiceFinanceProductTitle({
          ...it,
          termMonths,
          contract_term_months: termMonths ?? it.contract_term_months,
        });
      }
      return it.mortgage_product || it.product_name || it.name || "Unnamed product";
    })(),
    displayTitle: normType === "IF" ? formatInvoiceFinanceProductTitle({
      ...it,
      termMonths,
      contract_term_months: termMonths ?? it.contract_term_months,
    }) : null,
    rate: interestRate,
    // IF API: apr (e.g. 10.86). Fall back to interest_rate / discount_rate_percent only if needed.
    apr:
      normType === "IF"
        ? (firstFinite(it.apr, it.ratePct, it.aprc) ?? interestRate)
        : (parsePercentage(it.apr ?? it.ratePct) ??
          (typeof it.apr === "number" ? it.apr : interestRate)),
    // IF API: net_advance
    netAdvance: firstFinite(it.netAdvance, it.net_advance),
    // IF API: min_service_fee is a £ amount (e.g. 495)
    minServiceFee: firstFinite(it.minServiceFee, it.min_service_fee, it.miniServiceFeePct),
    // IF API: cost_over_product_term
    costOverProductTerm: firstFinite(
      it.costOverProductTerm,
      it.cost_over_product_term,
      finalTotalCost
    ),
    // IF API: ip_percent (e.g. 85)
    invoicePurchasePct: firstFinite(
      it.invoicePurchasePct,
      it.invoice_purchase_pct,
      it.ip_percent
    ),
    dpAllowed: it.dpAllowed ?? it.dp_allowed,
    // IF API: derive review limit from facility_required when not stored
    reviewLimit: (() => {
      const existing = firstFinite(it.reviewLimit, it.review_limit);
      if (existing != null) return existing;
      if (normType !== "IF") return null;
      const facility = firstFinite(
        it.facility_required,
        it.facilityRequired,
        it.loan_amount,
        it.headlineAmount
      );
      return calcIfReviewLimit(facility);
    })(),
    additionalLoanAmount: firstFinite(
      it.additionalLoanAmount,
      it.additional_loan_amount,
      it.additional_borrowing_amount
    ),
    // IF API: facility_required / loan_amount act as headline facility
    headlineAmount: firstFinite(
      it.headlineAmount,
      it.headline_amount,
      it.facility_required,
      it.facilityRequired,
      it.loan_amount
    ),
    // IF API: service_fee_percent (e.g. 0.97) — not simple_service_fee (often wrongly 97)
    annualCostPct:
      normType === "IF"
        ? resolveIfServiceFeePercent(it)
        : firstFinite(it.annualCostPct, it.annual_cost_pct, it.simple_service_fee),
    utilisationPct: (() => {
      if (normType !== "IF") return firstFinite(it.utilisationPct, it.utilisation_pct);
      const existing = firstFinite(it.utilisationPct, it.utilisation_pct);
      if (existing != null && existing > 0) return existing;
      return ifFeeEstimates?.utilisationPct ?? null;
    })(),
    utilisedFacility: (() => {
      if (normType !== "IF") return firstFinite(it.utilisedFacility, it.utilised_facility);
      const existing = firstFinite(it.utilisedFacility, it.utilised_facility);
      if (existing != null && existing > 0) return existing;
      return ifFeeEstimates?.utilisedFacility ?? null;
    })(),
    discountFeeAmount: (() => {
      if (normType !== "IF") return firstFinite(it.discountFeeAmount, it.discount_fee_amount);
      const existing = firstFinite(it.discountFeeAmount, it.discount_fee_amount);
      if (existing != null && existing > 0) return existing;
      return ifFeeEstimates?.discountFeeAmount ?? null;
    })(),
    serviceFeeAmount: (() => {
      if (normType !== "IF") return firstFinite(it.serviceFeeAmount, it.service_fee_amount);
      const existing = firstFinite(it.serviceFeeAmount, it.service_fee_amount);
      if (existing != null && existing > 0) return existing;
      return ifFeeEstimates?.serviceFeeAmount ?? null;
    })(),
    // IF API: arrangement_fee / product_fee_amount / total_fees
    arrangementFeeAmount: firstFinite(
      it.arrangementFeeAmount,
      it.arrangement_fee_amount,
      it.arrangement_fee,
      it.product_fee_amount,
      it.total_fees
    ),
    renewalFeeAmount: firstFinite(it.renewalFeeAmount, it.renewal_fee_amount),
    fixedFeeAmount: firstFinite(it.fixedFeeAmount, it.fixed_fee_amount),
    abCalc,
    // Persist AB fee % / £ so full details can recompute like sourcing
    arrangement_fee_term: firstFinite(
      it.arrangement_fee_term,
      it.arrangementFeeTerm,
      abCalc?.arrangementFeePct,
      it._raw?.arrangement_fee_term
    ),
    ab_arrangement_fee: firstFinite(
      abCalc?.arrangementFee,
      it.ab_arrangement_fee
    ),
    ab_cost_over_product_term: firstFinite(
      abCalc?.costOverTerm,
      it.ab_cost_over_product_term,
      it.ab_cost_over_term
    ),
    ab_cost_over_term: firstFinite(
      abCalc?.costOverTerm,
      it.ab_cost_over_product_term,
      it.ab_cost_over_term
    ),
    sector: it.sector ?? it._raw?.sector ?? null,
    projected_turnover: firstFinite(it.projected_turnover, it.projectedTurnover),
    // IF API: facility_required (fallback loan_amount)
    facility_required: firstFinite(
      it.facility_required,
      it.facilityRequired,
      it.headlineAmount,
      it.loan_amount
    ),
    // IF API may still store ledger Part/Full in facility_type — resolve from confidential flags
    facility_type: normType === "IF"
      ? resolveInvoiceFinanceFacilityType({ ...it, termMonths })
      : (it.facility_type ?? it.facilityType ?? null),
    // Keep ledger separately when API puts Part/Full in facility_type
    part_full_fund_ledger:
      it.part_full_fund_ledger ||
      (["part", "full"].includes(String(it.facility_type || "").trim().toLowerCase())
        ? it.facility_type
        : null),
    net_advance: firstFinite(it.net_advance, it.netAdvance),
    additional_borrowing_amount: firstFinite(
      it.additional_borrowing_amount,
      it.additionalBorrowingAmount,
      it.additionalLoanAmount
    ),
    additional_borrowing: it.additional_borrowing ?? 0,
    confidential_facility: it.confidential_facility ?? it.confidentialFacility ?? 0,
    confidential: it.confidential ?? 0,
    disclosure: it.disclosure ?? 0,
    ip_percent: firstFinite(it.ip_percent, it.invoicePurchasePct, it.invoice_purchase_pct),
    discount_rate_percent: firstFinite(
      it.discount_rate_percent,
      it.discountRatePercent,
      interestRate
    ),
    service_fee_percent: resolveIfServiceFeePercent(it),
    simple_service_fee: resolveIfServiceFeePercent(it),
    min_service_fee: firstFinite(it.min_service_fee, it.minServiceFee, it.miniServiceFeePct),
    rateType: rateType, // Rate type: Fixed, Variable, Discounted (for chip display)
    termMonths,
    contract_term_months: normType === "IF" ? termMonths : (it.contract_term_months ?? null),
    productTerm: productTermYears, // Product term (rate period), NOT mortgage term
    productTermRaw,
    mortgageTerm: mortgageTermYears, // Mortgage term (loan duration) from sourcing page
    mortgage_term: mortgageTermStr || (mortgageTermYears ? `${mortgageTermYears} yrs` : null),
    ltv: ltv,
    monthlyCost: finalMonthlyCost,
    totalCost: finalTotalCost,
    arrangement_fee_type: it.arrangement_fee_type ??
      it.product_fee_type ??
      it.fee_type ??
      it._raw?.arrangement_fee_type ??
      it._raw?.product_fee_type ??
      it._raw?.fee_type ??
      it._raw?.product_fee_type_selector,
    arrangement_fee: finalArrangementFee,
    arrangement_fee_percent: (() => {
      let pct = it.arrangement_fee_percent ?? it.product_fee_percent ?? it.fee_percent ??
        it._raw?.arrangement_fee_percent ?? it._raw?.product_fee_percent ?? it._raw?.fee_percent;
      if (pct != null && pct !== "") return Number(pct);
      const fromAmt = it.product_fee_amount ?? it._raw?.product_fee_amount;
      if (fromAmt != null && fromAmt !== "") {
        const n = Number(fromAmt);
        if (Number.isFinite(n) && n >= 0 && n <= 100) return n;
      }
      if (isPctType && finalArrangementFee === 0) return 0;
      return pct;
    })(),
    min_arrangement_fee: it.min_arrangement_fee ??
      it.min_fee ??
      it.product_min_fee ??
      it._raw?.min_arrangement_fee ??
      it._raw?.min_fee ??
      it._raw?.product_min_fee ??
      it._raw?.arrangement_min_fee,
    aprc: parsePercentage(it.aprc) ?? (typeof it.aprc === "number" ? it.aprc : null),
    loan_amount: finalLoanAmount,
    mortgageAmount: finalLoanAmount,
    // ICR from backend (percentage) - keep exact value including 0 for display
    icr_rate: (it.icr_rate != null || it.icr != null) ? safeNum(it.icr_rate ?? it.icr) : null,
    total_fees: safeNum(it.total_fees || it.totalFees) > 0 ? safeNum(it.total_fees || it.totalFees) : finalArrangementFee,
    fees: {
      arrangement: safeNum(finalArrangementFee),
      valuation: runSafeNum(it.fees?.valuation),
      other: runSafeNum(it.fees?.other)
    },
    shortlisted_erc: ercs, // Early Repayment Charges
    highlights: Array.isArray(it.highlights) ? it.highlights : [],
    addedAt: typeof it.addedAt === "number" ? it.addedAt : (it.creation ? new Date(it.creation).getTime() : Date.now()),
    status: it.status || "Pending",
    // Loan purpose from product (Purchase, Remortgage, Product Transfer)
    loan_purpose: it.loan_purpose || it.loanPurpose || it.loan_purpose_type || it._raw?.loan_purpose || it._raw?.loanPurpose || it._raw?.loan_purpose_type || "Purchase",
    loanPurpose: it.loan_purpose || it.loanPurpose || it.loan_purpose_type || it._raw?.loan_purpose || it._raw?.loanPurpose || it._raw?.loan_purpose_type || "Purchase",
    // Store original product data for reference
    _raw: it._raw || it,

    // ✅ SIMPLIFIED - USE BACKEND VALUES ONLY
    repayment_type: it.repayment_type || it.repaymentType || "Repayment",
    repaymentType: it.repaymentType || it.repayment_type || "Repayment",
    property_value: it.property_value ?? it.propertyValue ?? it._raw?.property_value ?? it._raw?.propertyValue ?? null,
    propertyValue: it.propertyValue ?? it.property_value ?? it._raw?.propertyValue ?? it._raw?.property_value ?? null,
  };
};

/* ======================================================
   Styling Components (from BorrowerSourcing.jsx)
====================================================== */
const GradientChip = ({ children }) => (
  <span className="inline-flex rounded-full p-[1px] bg-gradient-to-r from-sky-500 via-cyan-500 to-teal-500">
    <span className="px-3 py-1 rounded-full bg-white text-slate-900 text-[12px] font-semibold">
      {children}
    </span>
  </span>
);

const GreenTick = ({ compact }) => (
  <span
    className={
      "inline-flex shrink-0 items-center justify-center rounded-full bg-green-500 text-white " +
      (compact ? "h-2.5 w-2.5" : "h-4 w-4")
    }
    aria-hidden
  >
    <Check className={compact ? "h-1.5 w-1.5" : "h-2.5 w-2.5"} strokeWidth={3} />
  </span>
);

const formatIfChipMoney = (n) => {
  const amount = Number(n);
  if (!Number.isFinite(amount)) return "—";
  if (amount >= 1_000_000) {
    const millions = amount / 1_000_000;
    return `${millions % 1 === 0 ? millions.toFixed(0) : millions.toFixed(1)}M`;
  }
  return amount.toLocaleString("en-GB", { maximumFractionDigits: 0 });
};

const IfProductChips = ({ product, compact = false }) => {
  const ip = Number(product?.invoicePurchasePct);
  const reviewLimit = Number(product?.reviewLimit);
  const additionalLoan = Number(product?.additionalLoanAmount);
  const dpAllowed = product?.dpAllowed === "Yes" || product?.dpAllowed === true;
  const Chip = compact
    ? ({ children }) => (
        <span className="inline-flex shrink-0 rounded-full p-[1px] bg-gradient-to-r from-sky-500 via-cyan-500 to-teal-500">
          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-white text-slate-900 text-[9px] font-semibold leading-none whitespace-nowrap">
            {children}
          </span>
        </span>
      )
    : GradientChip;
  return (
    <div className={`flex items-center ${compact ? "flex-nowrap gap-1" : "flex-wrap gap-2"}`}>
      {Number.isFinite(ip) ? <Chip>{`IP ${Math.round(ip)}%`}</Chip> : null}
      <Chip>
        <span className="inline-flex items-center gap-0.5 whitespace-nowrap">
          DP{dpAllowed ? <GreenTick compact={compact} /> : null}
        </span>
      </Chip>
      {Number.isFinite(reviewLimit) ? (
        <Chip>{`Review limit £${formatIfChipMoney(reviewLimit)}`}</Chip>
      ) : null}
      {Number.isFinite(additionalLoan) && additionalLoan > 0 ? (
        <span className={"inline-flex shrink-0 items-center rounded-full border border-green-200 bg-green-50 text-green-700 font-semibold whitespace-nowrap " + (compact ? "px-1.5 py-0.5 text-[9px] leading-tight" : "px-3 py-1 text-[12px]")}>
          <span className="inline-flex items-center gap-0.5 whitespace-nowrap">
            {compact
              ? `AB £${formatIfChipMoney(additionalLoan)}`
              : `Additional loan £${additionalLoan.toLocaleString("en-GB", { maximumFractionDigits: 0 })}`}
            <GreenTick compact={compact} />
          </span>
        </span>
      ) : null}
    </div>
  );
};

// Helper to format arrangement fee display — mirror sourcing: show % when fee is percentage (e.g. 4%), £ when pound
const formatArrangementFeeDisplay = (product) => {
  const rawProduct = product?._raw || product;

  const feeType = product?.arrangement_fee_type ||
    product?.product_fee_type ||
    product?.fee_type ||
    rawProduct?.arrangement_fee_type ||
    rawProduct?.product_fee_type ||
    rawProduct?.fee_type ||
    rawProduct?.product_fee_type_selector ||
    "";

  const feeAmount = product?.arrangement_fee ??
    product?.arrangementFee ??
    product?.fees?.arrangement ??
    rawProduct?.arrangement_fee ??
    rawProduct?.arrangementFee ??
    null;

  const productFeeAmount = product?.product_fee_amount ?? rawProduct?.product_fee_amount ?? null;

  const feePercent = product?.arrangement_fee_percent ??
    product?.product_fee_percent ??
    product?.fee_percent ??
    rawProduct?.arrangement_fee_percent ??
    rawProduct?.product_fee_percent ??
    rawProduct?.fee_percent;

  const minFee = product?.min_arrangement_fee ||
    product?.min_fee ||
    product?.product_min_fee ||
    rawProduct?.min_arrangement_fee ||
    rawProduct?.min_fee ||
    rawProduct?.product_min_fee ||
    rawProduct?.arrangement_min_fee;

  const type = String(feeType).trim().toLowerCase();
  const amount = feeAmount != null ? safeNum(feeAmount) : null;
  const pctFromPercent = firstFinite(feePercent);
  const pctFromProductFeeAmount = productFeeAmount != null && productFeeAmount !== "" ? safeNum(productFeeAmount) : null;
  const percent = pctFromPercent != null && Number.isFinite(pctFromPercent)
    ? pctFromPercent
    : (pctFromProductFeeAmount != null && Number.isFinite(pctFromProductFeeAmount) && pctFromProductFeeAmount >= 0 && pctFromProductFeeAmount <= 100)
      ? pctFromProductFeeAmount
      : null;
  const min = firstFinite(minFee);

  const isPercentageType = type.includes("percent") || type === "%" || type === "percentage";

  if (amount == null && (percent == null || !Number.isFinite(percent))) {
    return "—";
  }

  if (type === "pound" || type === "gbp" || type === "" || !type) {
    if (amount > 0 && amount <= 100) {
      return `${amount}%`;
    }
  }

  if (isPercentageType) {
    if (percent != null && Number.isFinite(Number(percent))) return `${Number(percent)}%`;
    if (amount != null && Number(amount) <= 100) return `${Number(amount)}%`;
    return "0%";
  }

  if (type.includes("min") || type === "mini fee" || type === "minimum") {
    if (min != null && Number.isFinite(Number(min))) return `Min £${Number(min).toLocaleString()}`;
    if (amount != null && Number.isFinite(Number(amount))) return `Min £${Number(amount).toLocaleString()}`;
    return "£0";
  }

  if (percent != null && Number.isFinite(percent) && percent >= 0 && percent <= 100) {
    return `${Number(percent)}%`;
  }

  if (amount != null && Number.isFinite(Number(amount))) {
    const n = Number(amount);
    return n === 0 ? "£0.00" : `£${n.toLocaleString()}`;
  }
  return "—";
};

// Helper to format rate type for display (Fixed, Variable, Discounted)
// Maps common rate type variations to standard display format
const formatRateType = (rateType) => {
  if (!rateType) return null;
  const str = String(rateType).trim();
  if (!str) return null;

  const normalized = str.toLowerCase();

  // Map common variations to standard display names
  if (normalized.includes("fixed")) return "Fixed";
  if (normalized.includes("variable")) return "Variable";
  if (normalized.includes("discount") || normalized.includes("discounted")) return "Discounted";
  if (normalized.includes("tracker")) return "Tracker";

  // If it's already a valid type, capitalize properly
  if (["fixed", "variable", "discounted", "tracker"].includes(normalized)) {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }

  // Default: capitalize first letter, rest lowercase
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

const ChipRow = ({ term, type, ltv, productTermRaw }) => {
  const isLifetimeProduct = normalizeFilterToken(productTermRaw) === "lifetime";
  const termLabel = isLifetimeProduct ? "Lifetime" : term ? `${term} yr` : null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {termLabel ? <GradientChip>{termLabel}</GradientChip> : null}
      {type ? <GradientChip>{formatRateType(type) || type}</GradientChip> : null}
      {typeof ltv === "number" ? <GradientChip>{`LTV ${ltv}%`}</GradientChip> : null}
    </div>
  );
};

/* Sort dropdown - same options as sourcing page; IF uses APR instead of Interest rate */
const SORT_OPTIONS_BTL = [
  { key: "cost", label: "Cost over product term" },
  { key: "repayment", label: "Monthly payments" },
  { key: "fee", label: "Arrangement fee" },
  { key: "rate", label: "Interest rate" },
];

const SORT_OPTIONS_IF = [
  { key: "cost", label: "Cost over product term" },
  { key: "repayment", label: "Monthly payments" },
  { key: "fee", label: "Arrangement fee" },
  { key: "rate", label: "APR" },
];

function SortControl({ sortBy, setSortBy, options = SORT_OPTIONS_BTL }) {
  const [open, setOpen] = React.useState(false);
  const btnRef = React.useRef(null);
  const popRef = React.useRef(null);

  React.useEffect(() => {
    const onDoc = (e) => {
      const t = e.target;
      if (popRef.current?.contains(t) || btnRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const current = options.find((i) => i.key === sortBy)?.label ?? "Sort by";

  return (
    <div className="relative inline-block">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold shadow-sm hover:shadow md:px-4"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <SlidersHorizontal className="h-4 w-4 text-slate-600" />
        <span className="text-slate-800">{current}</span>
      </button>
      {open && (
        <div
          ref={popRef}
          className="absolute right-0 z-30 mt-2 w-[260px] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl"
          role="listbox"
        >
          {options.map((it) => {
            const active = it.key === sortBy;
            return (
              <button
                key={it.key}
                role="option"
                aria-selected={active}
                onClick={() => {
                  setSortBy(it.key);
                  setOpen(false);
                }}
                className={
                  "w-full flex items-center justify-between px-4 py-3 text-sm " +
                  (active ? "bg-sky-50 text-sky-800" : "hover:bg-slate-50 text-slate-800")
                }
              >
                <span>{it.label}</span>
                {active ? <Check className="h-4 w-4" /> : null}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function EmptyState({ label, hint, onNavigateToSourcing }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center">
      <div className="mx-auto w-12 h-12 rounded-full bg-sky-50 grid place-items-center">
        <Info className="w-6 h-6 text-sky-600" />
      </div>
      <h2 className="mt-3 text-lg font-semibold text-slate-900">{label}</h2>
      <p className="mt-1 text-sm text-slate-600">{hint}</p>
      {onNavigateToSourcing && (
        <button
          onClick={onNavigateToSourcing}
          className="mt-6 inline-flex items-center gap-2 rounded-xl bg-sky-600 px-6 py-3 text-sm font-semibold text-white hover:bg-sky-700 transition-colors shadow-sm"
        >
          <Plus className="h-4 w-4" />
          Add Products
        </button>
      )}
    </div>
  );
}

const ASK_ABOUT_PRODUCT_EMAIL = "admin@dynesourcer.co.uk";

function productAskTitle(product) {
  if (!product) return "this product";
  if (product.displayTitle) return product.displayTitle;
  if (normalizeProductType(product) === "IF") {
    return formatInvoiceFinanceProductTitle(product);
  }
  return product.name || product.product_name || product.productName || String(product.id || "this product");
}

function buildAskAboutProductMailto(product) {
  const productName = productAskTitle(product);
  const productId = product?.id || product?.product_id || "";
  const borrowerEmail = getBorrowerEmail() || "";
  const lines = [
    "Hi Dynesourcer team,",
    "",
    "I have a question about this shortlisted product:",
    productId ? `Product ID: ${productId}` : null,
    borrowerEmail ? `Borrower: ${borrowerEmail}` : null,
    "",
    "Please get in touch about this product.",
    "",
    "Kind regards,",
  ].filter((line) => line !== null);

  return `mailto:${ASK_ABOUT_PRODUCT_EMAIL}?subject=${encodeURIComponent(
    `Question about ${productName}`
  )}&body=${encodeURIComponent(lines.join("\n"))}`;
}

function shortlistProductIds(products = []) {
  return products
    .map((product) => product?.product_id || product?.mortgage_product || product?.id)
    .filter(Boolean)
    .map((id) => String(id).trim());
}

function CallbackRequestDialog({
  open,
  name,
  phone,
  productIds = [],
  error,
  loading = false,
  done = false,
  skipForm = false,
  onNameChange,
  onPhoneChange,
  onConfirm,
  onCancel,
}) {
  if (!open) return null;
  const phoneOk = isValidPhone(phone, { required: true });
  const canSubmit = Boolean(String(name || "").trim()) && phoneOk && !loading;
  return (
    <div className="fixed inset-0 z-[80]">
      <div className="absolute inset-0 bg-black/45" onClick={loading ? undefined : onCancel} aria-hidden />
      <div className="absolute inset-0 grid place-items-center p-4">
        <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl">
          {done ? (
            <div className="text-center py-2">
              <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                <Check className="h-6 w-6" />
              </div>
              <h3 className="mt-3 text-base font-bold text-slate-900">Callback requested</h3>
              <p className="mt-1 text-sm text-slate-600">
                Our team will review your shortlist and call you back.
              </p>
              <button
                type="button"
                onClick={onCancel}
                className="mt-5 inline-flex items-center justify-center rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700"
              >
                Close
              </button>
            </div>
          ) : skipForm ? (
            <div className="text-center py-4">
              <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-sky-100 text-sky-700">
                {loading ? <Loader2 className="h-6 w-6 animate-spin" /> : <Phone className="h-6 w-6" />}
              </div>
              <h3 className="mt-3 text-base font-bold text-slate-900">Request a callback</h3>
              <p className="mt-1 text-sm text-slate-600">
                {error || "Sending your callback request..."}
              </p>
              {error ? (
                <button
                  type="button"
                  onClick={onCancel}
                  className="mt-5 inline-flex items-center justify-center rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Close
                </button>
              ) : null}
            </div>
          ) : (
            <>
              <div className="flex items-start gap-3">
                <div className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-full bg-sky-100 text-sky-700">
                  <Phone className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-base font-bold text-slate-900">Request a callback</h3>
                  <p className="mt-1 text-sm text-slate-600">
                    Confirm your name and mobile number. Our team will review your shortlist and call you back.
                  </p>
                </div>
              </div>
              <div className="mt-4 space-y-3">
                <label className="block">
                  <span className="text-xs font-semibold text-slate-600">Full name</span>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => onNameChange?.(e.target.value)}
                    disabled={loading}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-sky-200 disabled:bg-slate-50"
                    placeholder="Your name"
                    autoComplete="name"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-slate-600">Mobile number</span>
                  <div className="mt-1">
                    <PhoneField
                      id="callback-phone"
                      name="callback-phone"
                      value={phone}
                      onChange={onPhoneChange}
                      disabled={loading}
                      required
                    />
                  </div>
                </label>
                {productIds.length ? (
                  <p className="text-xs text-slate-500">
                    Product IDs: <span className="font-mono text-slate-700">{productIds.join(", ")}</span>
                  </p>
                ) : null}
                {error ? <p className="text-xs text-rose-600">{error}</p> : null}
              </div>
              <div className="mt-5 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={onCancel}
                  disabled={loading}
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={onConfirm}
                  disabled={!canSubmit}
                  className="inline-flex items-center gap-2 rounded-lg bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-60"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Phone className="h-4 w-4" />}
                  Request callback
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ======================================================
   Card — Apply now, Ask about this product
====================================================== */
const DetailRow = ({ label, value, strong = false }) => (
  <div className="flex items-start justify-between gap-3 min-w-0 w-full py-0.5">
    <dt className="text-xs md:text-sm text-slate-600 min-w-0 flex-1 break-words leading-snug">{label}</dt>
    <dd
      className={
        "text-xs md:text-sm text-right shrink-0 whitespace-nowrap tabular-nums " +
        (strong ? "font-semibold text-slate-900" : "text-slate-900")
      }
    >
      {value}
    </dd>
  </div>
);

const formatIfPercentRaw = (n) => {
  if (!Number.isFinite(Number(n))) return "—";
  const rounded = Math.round(Number(n) * 1e8) / 1e8;
  if (Number.isInteger(rounded)) return `${rounded}`;
  return rounded.toFixed(8).replace(/\.?0+$/, "");
};

const getIfMinServiceFeeAmount = (product, facilityAmount) => {
  const raw = Number(
    product?.minServiceFee ?? product?.min_service_fee ?? product?.miniServiceFeePct
  );
  const baseAmount = Number(facilityAmount);
  if (!Number.isFinite(raw) || raw <= 0) return null;
  // API stores min_service_fee as £ (e.g. 495). Values under 100 may be % of facility.
  if (raw < 100) {
    if (!Number.isFinite(baseAmount) || baseAmount <= 0) return null;
    return Math.round((baseAmount * raw) / 100);
  }
  return Math.round(raw);
};

function OverflowMenu({ items = [], disabled = false, align = "right", buttonClassName = "" }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative shrink-0" ref={rootRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((value) => !value);
        }}
        className={
          buttonClassName ||
          "inline-flex items-center justify-center w-9 h-9 rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
        }
        aria-label="More options"
        aria-haspopup="menu"
        aria-expanded={open}
        title="More options"
      >
        <MoreHorizontal className="w-5 h-5" />
      </button>
      {open ? (
        <div
          role="menu"
          className={`absolute z-30 mt-1 min-w-[10.5rem] rounded-xl border border-slate-200 bg-white py-1 shadow-lg ${
            align === "left" ? "left-0" : "right-0"
          }`}
        >
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              disabled={item.disabled}
              onClick={(event) => {
                event.stopPropagation();
                setOpen(false);
                item.onClick?.();
              }}
              className={`w-full px-3 py-2 text-left text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed ${
                item.tone === "danger"
                  ? "text-rose-600 hover:bg-rose-50"
                  : "text-slate-700 hover:bg-slate-50"
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function ConfirmDialog({ open, title, message, confirmLabel, cancelLabel = "Cancel", onConfirm, onCancel, loading = false }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80]">
      <div className="absolute inset-0 bg-black/45" onClick={loading ? undefined : onCancel} aria-hidden />
      <div className="absolute inset-0 grid place-items-center p-4">
        <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-full bg-rose-100 text-rose-700">
              <AlertTriangle className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-bold text-slate-900">{title}</h3>
              <p className="mt-1 text-sm text-slate-600 whitespace-pre-line">{message}</p>
            </div>
          </div>
          <div className="mt-5 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-3 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ShortlistCard({ product, onApply, onRemove, disableApply, isLoading, isOpen, onToggle, isSelected, onSelect, isLinkedToApplication, linkedAppInfo }) {
  const detailsSectionRef = React.useRef(null);

  // When "Full details" is opened, scroll the details section into view
  React.useEffect(() => {
    if (isOpen && detailsSectionRef.current) {
      detailsSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [isOpen]);

  const rate = typeof product.rate === "number" ? product.rate : Number(product.rate || 0);
  const repayment = product.monthlyCost ?? 0;
  const fee = product.fees?.arrangement ?? 0;
  const totalCost = product.totalCost ?? 0;

  // Context for calculations (assumed passed from Sourcing)
  const mortgageAmount = product.mortgageAmount || product.loan_amount || 0;
  // ICR from backend (percentage) - use icr_rate / icr, display as % like sourcing
  const icrVal = product.icr_rate != null || product.icr != null
    ? (Number(product.icr_rate ?? product.icr))
    : null;
  const hasValidIcr = icrVal != null && Number.isFinite(icrVal);

  const Num = (v) =>
    typeof v === "number" && isFinite(v)
      ? v.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : "—";

  // Product term (rate period) for chip display at top
  let productTermYears = product.productTerm;
  if (!productTermYears && typeof product.termMonths === "number" && product.termMonths > 0) {
    productTermYears = parseFloat((product.termMonths / 12).toFixed(1));
  }

  // Mortgage term (loan duration) from sourcing page for details section
  const mortgageTermYears = product.mortgageTerm || parseTermYears(product.mortgage_term) || null;

  // Get ERCs - already normalized by normaliseItem to { year: "1", val: "7%" } format
  // or coming from backend in normalized state.
  const ercs = Array.isArray(product.shortlisted_erc)
    ? product.shortlisted_erc
    : [];

  const productRadioClassName =
    "w-5 h-5 md:w-6 md:h-6 shrink-0 cursor-pointer accent-sky-600 focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed";

  const isIf = normalizeProductType(product) === "IF";
  const aprPct = Number(product.apr ?? product.ratePct ?? product.rate);
  const netAdvance = Number(product.netAdvance ?? product.net_advance);
  const minServiceFee = Number(product.minServiceFee ?? product.min_service_fee ?? product.miniServiceFeePct);
  const costOverTerm = Number(product.costOverProductTerm ?? product.totalCost);
  const IfPound = (v) =>
    Number.isFinite(Number(v))
      ? `£${Number(v).toLocaleString("en-GB", { maximumFractionDigits: 0 })}`
      : "—";
  const IfPct = (v) => (Number.isFinite(Number(v)) ? `${Number(v).toFixed(2)}%` : "—");

  return (
    <div className="mb-2.5">
      <div
        className={`relative rounded-2xl border-2 bg-white transition-all cursor-pointer ${isSelected
          ? "border-sky-500 shadow-lg ring-2 ring-sky-200"
          : disableApply
            ? "border-slate-200 opacity-60 hover:opacity-80"
            : "border-slate-300 hover:border-sky-400 hover:shadow-md"
          }`}
        onClick={(e) => {
          // Don't trigger selection if clicking on buttons or links
          if (e.target.closest('button') || e.target.closest('a') || isLoading) return;
          // Allow clicking on disabled products to switch to them
          onSelect?.();
        }}
      >
        {/* Mobile: compact header — select, chips */}
        <div className="flex md:hidden flex-col gap-2 rounded-t-2xl border-b border-slate-200 px-3 py-2">
          <div className="flex items-center justify-between gap-2">
            <label
              className="flex items-center shrink-0 cursor-pointer"
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="radio"
                name="selectedProduct"
                checked={isSelected}
                onChange={() => onSelect && onSelect()}
                disabled={isLoading}
                className={productRadioClassName}
              />
              <span
                className={`ml-2 text-xs font-semibold whitespace-nowrap ${isSelected ? "text-sky-700" : "text-slate-700"}`}
              >
                Select
              </span>
            </label>
            <div className="flex items-center gap-1.5 shrink-0">
              {isLinkedToApplication ? (
                <div className="text-right">
                  <span className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2.5 py-1 text-[10px] font-semibold text-amber-800">
                    <AlertCircle className="h-3.5 w-3.5" />
                    Linked
                  </span>
                  {linkedAppInfo && (
                    <span className="mt-0.5 block text-[10px] text-amber-600 font-medium truncate max-w-[120px]">
                      {linkedAppInfo.appId}
                    </span>
                  )}
                </div>
              ) : (
                <OverflowMenu
                  items={[{ label: "Remove", tone: "danger", onClick: onRemove }]}
                  buttonClassName="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
                />
              )}
            </div>
          </div>
          <div className="min-w-0 flex items-center gap-1.5 flex-wrap">
            {isIf ? (
              <IfProductChips product={product} compact />
            ) : (
              <>
                {normalizeProductType(product) === "CBTL" ? <TypeBadge t="CBTL" /> : null}
                <PropertyTypeChips product={product} compact />
                <ChipRow
                  term={productTermYears}
                  type={product.rateType || product.rate_type || product.product_type || null}
                  ltv={typeof product.ltv === "number" ? Math.round(product.ltv) : undefined}
                  productTermRaw={product.productTermRaw}
                />
              </>
            )}
          </div>
        </div>

        {/* Desktop: original header */}
        <div className="hidden md:flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between rounded-t-2xl border-b border-slate-200 px-3 py-2.5">
          <div className="min-w-0 flex items-center gap-3 flex-wrap">
            <label
              className={`flex items-center shrink-0 cursor-pointer transition-colors ${isSelected ? "text-sky-700" : "hover:text-sky-700"}`}
            >
              <input
                type="radio"
                name="selectedProduct"
                checked={isSelected}
                onChange={() => onSelect && onSelect()}
                disabled={isLoading}
                className={productRadioClassName}
              />
              <span
                className={`ml-2.5 text-sm font-semibold whitespace-nowrap ${isSelected ? "text-sky-700" : "text-slate-700"}`}
              >
                Select
              </span>
            </label>
            <div className="min-w-0 flex-1 basis-0 sm:basis-auto">
              {isIf ? (
                <IfProductChips product={product} />
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  {normalizeProductType(product) === "CBTL" ? <TypeBadge t="CBTL" /> : null}
                  <PropertyTypeChips product={product} />
                  <ChipRow
                    term={productTermYears}
                    type={product.rateType || product.rate_type || product.product_type || null}
                    ltv={typeof product.ltv === "number" ? Math.round(product.ltv) : undefined}
                    productTermRaw={product.productTermRaw}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-end sm:justify-start gap-2 md:gap-3 shrink-0">
            {isLinkedToApplication ? (
              <div className="flex flex-col items-end gap-1">
                <button
                  disabled
                  className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold border-2 border-amber-300 bg-amber-50 text-amber-800 cursor-not-allowed opacity-90"
                  title={linkedAppInfo ? `This product is linked to application ${linkedAppInfo.appId} (${linkedAppInfo.status})` : "This product is linked to an ongoing application"}
                >
                  <AlertCircle className="h-4 w-4" />
                  Linked to App
                </button>
                {linkedAppInfo && (
                  <div className="text-right">
                    <span className="text-xs text-amber-700 font-semibold block">
                      App: {linkedAppInfo.appId}
                    </span>
                    <span className="text-xs text-amber-600 font-medium block">
                      {linkedAppInfo.status}
                    </span>
                  </div>
                )}
              </div>
            ) : null}

            <button
              onClick={onApply}
              disabled={!!disableApply || isLoading || !isSelected}
              className={
                "inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold text-white transition " +
                (disableApply || isLoading || !isSelected
                  ? "bg-slate-300 cursor-not-allowed"
                  : "bg-sky-600 hover:bg-sky-700 shadow-sm")
              }
              title={!isSelected ? "Please select this product first" : disableApply ? "You can apply from one product only" : "Apply now"}
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Applying...
                </>
              ) : (
                "Apply now"
              )}
            </button>
            {!isLinkedToApplication ? (
              <OverflowMenu
                items={[{ label: "Remove", tone: "danger", onClick: onRemove }]}
              />
            ) : null}
          </div>
        </div>

        {/* Mobile: compact body — rate left, metrics right */}
        <div className="flex md:hidden items-stretch gap-2 px-3 py-2">
          <div className="w-[118px] shrink-0 flex flex-col items-center justify-center rounded-xl bg-sky-500 px-2 py-2.5 text-white shadow-sm">
            <div className="text-[10px] font-semibold leading-tight text-center">{isIf ? "APR" : "Interest rate"}</div>
            <div className="mt-0.5 text-xl font-extrabold leading-none">
              {Number.isFinite(isIf ? aprPct : rate) ? (isIf ? aprPct : rate).toFixed(2) : "—"}%
            </div>
          </div>
          <div className="flex-1 min-w-0 flex flex-col justify-center gap-1.5 py-0.5">
            {isIf ? (
              <>
                <div className="flex items-center justify-between gap-2 text-xs">
                  <span className="text-slate-500 shrink-0">Net Advance</span>
                  <span className="font-bold text-slate-900 truncate">{IfPound(netAdvance)}</span>
                </div>
                <div className="flex items-center justify-between gap-2 text-xs">
                  <span className="text-slate-500 shrink-0">Min. fee</span>
                  <span className="font-bold text-slate-900 truncate">{IfPound(minServiceFee)}</span>
                </div>
                <div className="flex items-center justify-between gap-2 text-xs">
                  <span className="text-slate-500 shrink-0">Cost over term</span>
                  <span className="font-bold text-slate-900 truncate">{IfPound(costOverTerm)}</span>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between gap-2 text-xs">
                  <span className="text-slate-500 shrink-0">Monthly</span>
                  <span className="font-bold text-slate-900 truncate">£{Num(repayment)}</span>
                </div>
                <div className="flex items-center justify-between gap-2 text-xs">
                  <span className="text-slate-500 shrink-0">Arrangement fee</span>
                  <span className="font-bold text-slate-900 truncate text-right">{formatArrangementFeeDisplay(product)}</span>
                </div>
                <div className="flex items-center justify-between gap-2 text-xs">
                  <span className="text-slate-500 shrink-0">Cost over term</span>
                  <span className="font-bold text-slate-900 truncate">£{Num(totalCost)}</span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Mobile: footer — apply */}
        <div className="md:hidden border-t border-slate-200 px-3 py-2">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onApply();
            }}
            disabled={!!disableApply || isLoading || !isSelected}
            className={
              "w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold text-white transition " +
              (disableApply || isLoading || !isSelected
                ? "bg-slate-300 cursor-not-allowed"
                : "bg-sky-600 hover:bg-sky-700 shadow-sm")
            }
            title={!isSelected ? "Please select this product first" : disableApply ? "You can apply from one product only" : "Apply now"}
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Applying...
              </>
            ) : (
              "Apply now"
            )}
          </button>
        </div>

        {/* Desktop: original body */}
        <div className="hidden md:block px-3 py-2.5">
          <div className="grid grid-cols-[max-content,1fr,1fr,1fr] gap-x-2 items-center">
            <div className="flex flex-col items-center justify-center rounded-xl bg-sky-500 px-6 py-4 min-h-[84px] text-white shadow-sm">
              <div className="text-[17px] font-semibold leading-tight">
                {isIf ? "APR" : "Interest rate"}
              </div>
              <div className="mt-0.5 text-[34px] font-extrabold leading-none">
                {Number.isFinite(isIf ? aprPct : rate) ? (isIf ? aprPct : rate).toFixed(2) : "—"}%
              </div>
            </div>

            <div className="flex flex-col items-center justify-center text-center">
              <div className="text-[17px] text-slate-600 leading-tight">
                {isIf ? "Net Advance" : "Monthly repayments"}
              </div>
              <div className="mt-0.5 text-[24px] font-extrabold text-slate-900 leading-none">
                {isIf ? IfPound(netAdvance) : `£${Num(repayment)}`}
              </div>
            </div>

            <div className="flex flex-col items-center justify-center text-center">
              <div className="text-[17px] text-slate-600 leading-tight">
                {isIf ? "Min. service fee" : "Arrangement fee"}
              </div>
              <div className="mt-0.5 text-[24px] font-extrabold text-slate-900 leading-none">
                {isIf ? IfPound(minServiceFee) : formatArrangementFeeDisplay(product)}
              </div>
            </div>

            <div className="flex flex-col items-center justify-center text-center">
              <div className="text-[17px] text-slate-600 leading-tight">
                Cost over product term
              </div>
              <div className="mt-0.5 text-[24px] font-extrabold text-slate-900 leading-none">
                {isIf ? IfPound(costOverTerm) : `£${Num(totalCost)}`}
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between border-t border-slate-200 px-3 py-2.5">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            className="inline-flex items-center justify-center gap-1.5 text-sm font-semibold text-sky-700 hover:text-sky-800 transition-colors"
          >
            {isOpen ? "Hide details" : "Full details"}
            <ChevronDown
              className={`h-4 w-4 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
            />
          </button>
          <a
            href={buildAskAboutProductMailto(product)}
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-2 text-sm font-semibold text-white bg-green-600 hover:bg-green-700 shadow-sm transition"
            title="Ask about this product"
          >
            <Mail className="h-4 w-4" />
            Ask about this product
          </a>
        </div>

        {/* Full Details Section */}
        {isOpen && (
          <div ref={detailsSectionRef} className="border-t border-slate-200 bg-white p-0 rounded-b-2xl shadow-inner animate-in fade-in slide-in-from-top-2 duration-200">
            {isIf ? (
              (() => {
                const productId =
                  product.product_id || product.mortgage_product || product.id || "—";
                const facilityAmount = firstFinite(
                  product.facility_required,
                  product.facilityRequired,
                  product.headlineAmount,
                  product.mortgageAmount,
                  product.loan_amount
                );
                const reviewLimit =
                  firstFinite(product.reviewLimit, product.review_limit) ??
                  calcIfReviewLimit(facilityAmount);
                const minFeeAmount =
                  getIfMinServiceFeeAmount(product, facilityAmount ?? netAdvance) ??
                  (Number.isFinite(minServiceFee) && minServiceFee >= 100 ? minServiceFee : null);
                const contractTermLabel = Number.isFinite(Number(product.termMonths))
                  ? `${product.termMonths} months`
                  : "—";
                const ipPct =
                  Number.isFinite(Number(product.invoicePurchasePct))
                    ? `${Math.round(Number(product.invoicePurchasePct))}%`
                    : "—";
                const abCalc = product.abCalc || null;
                const additionalLoan = Number(
                  product.additionalLoanAmount ?? product.additional_borrowing_amount ?? 0
                );
                const showAdditionalBorrowing =
                  (Number.isFinite(additionalLoan) && additionalLoan > 0) || !!abCalc;
                const pound = (v) =>
                  Number.isFinite(Number(v))
                    ? `£${Number(v).toLocaleString("en-GB", { maximumFractionDigits: 0 })}`
                    : "—";

                return (
                  <div className="overflow-hidden rounded-b-2xl">
                    <div className="flex-shrink-0 flex items-center justify-between gap-2 bg-white border-b border-slate-200 px-3 py-2 md:px-6 md:py-4 md:min-h-[60px]">
                      <div className="min-w-0 flex-1">
                        <div className="text-[10px] md:text-xs font-semibold uppercase tracking-wide text-sky-600">
                          Product details
                        </div>
                        <div className="mt-0.5 text-sm md:text-lg font-semibold text-slate-900 truncate pr-2">
                          {product.displayTitle || formatInvoiceFinanceProductTitle(product)}
                        </div>
                        <div className="mt-0.5 text-[11px] md:text-xs text-slate-600">
                          Product ID:{" "}
                          <span className="font-semibold text-slate-900">{productId}</span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={onToggle}
                        aria-label="Close details"
                        className="flex-shrink-0 flex items-center justify-center rounded-xl bg-slate-200 text-slate-600 hover:bg-slate-300 active:bg-slate-400 font-semibold min-h-[36px] min-w-[36px] p-0 md:h-9 md:w-9 md:min-h-0 md:min-w-0 md:rounded-lg md:border md:border-slate-200 md:bg-transparent md:text-slate-700 md:hover:bg-slate-50"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>

                    <div className="px-3 py-2 pb-3 md:px-5 md:py-3 md:pb-4">
                      <div
                        className={`grid gap-2.5 md:gap-5 ${
                          showAdditionalBorrowing ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1"
                        }`}
                      >
                        <div className="min-w-0">
                          <dl className="grid grid-cols-2 md:grid-cols-1 gap-x-2 gap-y-1 md:gap-y-1.5 [&>*]:flex [&>*]:flex-col [&>*]:items-start [&>*]:gap-0 [&>*]:min-w-0 [&_dt]:text-[10px] md:[&_dt]:text-sm [&_dd]:text-left [&_dd]:text-[11px] md:[&_dd]:text-sm md:[&>*]:flex-row md:[&>*]:items-center md:[&>*]:justify-between md:[&_dd]:text-right">
                            <DetailRow
                              label="APR"
                              value={Number.isFinite(aprPct) ? `${aprPct.toFixed(2)}%` : "—"}
                              strong
                            />
                            <DetailRow label="Net Advance" value={pound(netAdvance)} strong />
                            <DetailRow
                              label="Min. service fee"
                              value={pound(minFeeAmount)}
                              strong
                            />
                            <DetailRow label="Contract term" value={contractTermLabel} />
                            <DetailRow
                              label={
                                <>
                                  <span className="md:hidden">IP</span>
                                  <span className="hidden md:inline">Initial percentage (IP)</span>
                                </>
                              }
                              value={ipPct}
                              strong
                            />
                            <DetailRow label="Review limit" value={pound(reviewLimit)} strong />
                            <DetailRow
                              label="Headline facility"
                              value={pound(
                                firstFinite(product.headlineAmount, facilityAmount)
                              )}
                              strong
                            />
                            <DetailRow
                              label="Service fee"
                              value={
                                Number.isFinite(
                                  Number(
                                    product.service_fee_percent ?? product.annualCostPct
                                  )
                                )
                                  ? `${formatIfPercentRaw(
                                      product.service_fee_percent ?? product.annualCostPct
                                    )}%`
                                  : "—"
                              }
                            />
                            <DetailRow
                              label="Facility utilisation"
                              value={
                                Number.isFinite(Number(product.utilisationPct))
                                  ? `${Number(product.utilisationPct)}%`
                                  : "—"
                              }
                            />
                            <DetailRow
                              label="Utilised facility"
                              value={pound(product.utilisedFacility)}
                            />
                            <DetailRow
                              label="Discount fee cost"
                              value={pound(product.discountFeeAmount)}
                            />
                            <DetailRow
                              label="Service fee cost"
                              value={pound(product.serviceFeeAmount)}
                            />
                            <DetailRow
                              label="Arrangement fee cost"
                              value={pound(
                                product.arrangementFeeAmount ??
                                  product.arrangement_fee ??
                                  product.product_fee_amount ??
                                  product.total_fees
                              )}
                            />
                            <DetailRow
                              label="Other fixed fees"
                              value={
                                Number.isFinite(Number(product.fixedFeeAmount))
                                  ? pound(product.fixedFeeAmount)
                                  : "—"
                              }
                            />
                            <DetailRow
                              label="Cost over product term"
                              value={pound(costOverTerm)}
                              strong
                            />
                          </dl>
                        </div>

                        {showAdditionalBorrowing ? (
                          abCalc ? (
                            <div className="min-w-0 md:border-l md:border-slate-200 md:pl-5 pt-1.5 md:pt-0 border-t border-slate-100 md:border-t-0">
                              <div className="text-[11px] md:text-xs font-semibold text-slate-900 mb-1 md:mb-1.5 inline-flex items-center gap-1.5 flex-wrap">
                                Additional Borrowing
                                <span className="rounded bg-sky-100 px-1.5 py-0.5 text-[9px] md:text-[10px] font-bold uppercase tracking-wide text-sky-700 leading-none">
                                  Capital &amp; Interest
                                </span>
                              </div>
                              <dl className="grid grid-cols-2 md:grid-cols-1 gap-x-2 gap-y-1 md:gap-y-1.5 [&>*]:flex [&>*]:flex-col [&>*]:items-start [&>*]:gap-0 [&>*]:min-w-0 [&_dt]:text-[10px] md:[&_dt]:text-sm [&_dd]:text-left [&_dd]:text-[11px] md:[&_dd]:text-sm md:[&>*]:flex-row md:[&>*]:items-center md:[&>*]:justify-between md:[&_dd]:text-right">
                                <DetailRow
                                  label="Loan amount"
                                  value={pound(
                                    abCalc.abPrincipal ??
                                      product.ab_principal ??
                                      additionalLoan
                                  )}
                                  strong
                                />
                                <DetailRow
                                  label="Interest rate"
                                  value={
                                    abCalc.interestRatePct != null
                                      ? `${abCalc.interestRatePct}%`
                                      : product.additional_borrowing_interest_rate != null
                                        ? `${product.additional_borrowing_interest_rate}%`
                                        : "—"
                                  }
                                />
                                <DetailRow
                                  label="First 12 months interest"
                                  value={pound(
                                    abCalc.first12MonthsInterest ??
                                      product.first_12_month_interest
                                  )}
                                />
                                <DetailRow
                                  label="Arrangement fee"
                                  value={pound(
                                    abCalc.arrangementFee ?? product.ab_arrangement_fee
                                  )}
                                />
                                <DetailRow
                                  label="Contract term"
                                  value={
                                    abCalc.termMonths != null
                                      ? `${abCalc.termMonths} months`
                                      : product.ab_term_months != null
                                        ? `${product.ab_term_months} months`
                                        : contractTermLabel
                                  }
                                />
                                <DetailRow
                                  label="Monthly repayment"
                                  value={pound(
                                    abCalc.monthlyRepayment ??
                                      product.monthly_repayments ??
                                      product.monthlyCost
                                  )}
                                />
                                <DetailRow
                                  label="Total repayable"
                                  value={pound(
                                    abCalc.totalRepayable ?? product.total_repayable
                                  )}
                                  strong
                                />
                                <DetailRow
                                  label="Cost over product term"
                                  value={pound(
                                    abCalc.costOverTerm ??
                                      product.ab_cost_over_product_term ??
                                      product.ab_cost_over_term ??
                                      costOverTerm
                                  )}
                                  strong
                                />
                              </dl>
                            </div>
                          ) : (
                            <div className="min-w-0 md:border-l md:border-slate-200 md:pl-5 pt-1.5 md:pt-0 border-t border-slate-100 md:border-t-0">
                              <div className="text-[11px] md:text-xs font-semibold text-slate-900 mb-1 md:mb-1.5">
                                Additional Borrowing
                              </div>
                              <p className="text-xs text-slate-500">
                                Not available for this product. Additional borrowing is only shown
                                for Capital Repayment products.
                              </p>
                            </div>
                          )
                        ) : null}
                      </div>

                      <p className="mt-2 md:mt-3 text-[10px] md:text-[11px] text-slate-500 leading-snug">
                        {showAdditionalBorrowing
                          ? "Cost over product term includes discount fee, service fee, arrangement fee, renewal fee, fixed fees and VAT where applicable, plus the first 12 months interest on additional borrowing where shown. Facility principal is not included."
                          : "Cost over product term is discount fee, service fee, arrangement fee, renewal fee, fixed fees and VAT where applicable. Facility principal is not included."}
                      </p>
                    </div>
                  </div>
                );
              })()
            ) : (
              <div className="p-6">
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-slate-900">{product.name}</h3>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                  {/* Left Column: Key Stats */}
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <DetailRow label="APRC" value={product.aprc != null && Number.isFinite(product.aprc) ? `${product.aprc.toFixed(2)}%` : "—"} />
                      <DetailRow label="Loan amount" value={`£${Num(mortgageAmount)}`} />
                      {(() => {
                        const propertyValue = product.property_value ?? product.propertyValue ?? product._raw?.property_value ?? product._raw?.propertyValue;
                        const pvNum = propertyValue != null && propertyValue !== "" ? Number(propertyValue) : null;
                        return pvNum != null && Number.isFinite(pvNum) ? (
                          <DetailRow label="Property value" value={`£${Num(pvNum)}`} />
                        ) : null;
                      })()}
                      <DetailRow label="Mortgage term" value={mortgageTermYears ? `${mortgageTermYears} yrs` : "—"} />
                      <DetailRow label="Monthly payment p/m" value={`£${Num(repayment)}`} />
                      <DetailRow label="Cost over product term" value={`£${Num(totalCost)}`} />
                      <DetailRow label="Product fee" value={formatArrangementFeeDisplay(product)} />
                      <DetailRow label="Total fees" value={product.total_fees != null || product.totalFees != null
                        ? `£${Num(safeNum(product.total_fees ?? product.totalFees))}`
                        : `£${Num(fee + safeNum(product.fees?.valuation) + safeNum(product.fees?.other))}`} />
                      <DetailRow label="DSC / ICR" value={hasValidIcr ? formatIcrPercent(icrVal) : "—"} />
                    </div>

                    <p className="text-[11px] text-slate-400 mt-4 leading-relaxed">
                      * Some lenders may charge additional fees which may increase the monthly payment.
                    </p>
                  </div>

                  {/* Right Column: ERCs */}
                  <div>
                    <h4 className="font-bold text-slate-900 mb-4 text-sm">Early repayment charges</h4>
                    <div className="space-y-2">
                      {ercs.length > 0 ? (
                        ercs.map((e, i) => (
                          <div key={i} className="flex justify-between py-1 text-sm">
                            <span className="text-slate-600">Year {e.year}</span>
                            <span className="font-semibold text-slate-900">{e.val}</span>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-slate-500">No ERC data available</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


/* ======================================================
   Main Component
====================================================== */
export default function ShortlistedModal() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loadingId, setLoadingId] = useState(null);
  const [error, setError] = useState(null);
  const [openDetailsFor, setOpenDetailsFor] = useState(null);
  const [retryKey, setRetryKey] = useState(0); // Key to force effect re-run
  const [linkedProducts, setLinkedProducts] = useState(new Map()); // Track products linked to ongoing applications: Map<productName, {appId, appName, status}>
  const [shortlistFullWarning, setShortlistFullWarning] = useState(false);
  const [dismissedShortlistFullWarning, setDismissedShortlistFullWarning] = useState(false);
  const [toast, setToast] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    type: null,
    productId: null,
  });
  const [callbackDialog, setCallbackDialog] = useState({
    open: false,
    name: "",
    phone: "",
    loading: false,
    error: "",
    done: false,
    skipForm: false,
  });
  const [borrowerContact, setBorrowerContact] = useState({ name: "", phone: "" });

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    try {
      if (sessionStorage.getItem("ds_shortlist_full_warning") === "1") {
        setDismissedShortlistFullWarning(false);
        setShortlistFullWarning(true);
      }
    } catch { /* ignore */ }
  }, []);

  // Fetch shortlisted products - Professional approach: Backend-first for authenticated users
  useEffect(() => {
    const loadShortlistedProducts = async () => {
      setLoading(true);
      setError(null);

      // Unauthenticated users: Use localStorage only (temporary storage)
      if (!isAuthenticated()) {
        const confirmed = safeReadArray(LS_SHORTLIST).map(normaliseItem).filter(Boolean);
        const pending = safeReadArray(LS_PENDING).map(normaliseItem).filter(Boolean);
        const base = (confirmed.length ? confirmed : pending).slice(0, 5);
        setItems(base);
        setLoading(false);
        return;
      }

      // Authenticated users: ALWAYS use backend (single source of truth)
      try {
        const result = await getShortlistedProducts({ limit: 50, with_erc: true });

        console.log("📋 ShortlistedProducts fetch result:", {
          success: result.success,
          itemsCount: result.data?.length || 0,
          hasData: Array.isArray(result.data) && result.data.length > 0
        });

        if (result.success && Array.isArray(result.data) && result.data.length > 0) {
          // OPTIMIZATION: Use product_term directly from shortlisted API response
          // No need to fetch original products - product_term is already in the API response
          const normalized = result.data.map((p) => {
            // product_term should already be in the shortlisted API response
            // normaliseItem will extract it from: product_term_years, product_termsyears, or productTerm
            return normaliseItem(p);
          }).filter(Boolean);

          console.log("✅ Using backend shortlisted products (product_term from API):", {
            count: normalized.length,
            sample: normalized[0] ? {
              id: normalized[0].id,
              aprc: normalized[0].aprc,
              productTerm: normalized[0].productTerm,
              mortgageTerm: normalized[0].mortgageTerm,
              ercCount: normalized[0].shortlisted_erc?.length || 0,
              hasErc: Array.isArray(normalized[0].shortlisted_erc) && normalized[0].shortlisted_erc.length > 0,
              hasProductTerm: !!normalized[0].productTerm
            } : null
          });
          setItems(normalized);
        } else {
          // Backend returned empty - show empty state (NOT localStorage fallback)
          // This is the correct behavior: empty means no products, not a reason to show stale localStorage data
          console.log("ℹ️ Backend returned empty shortlist - showing empty state");
          setItems([]);
        }
      } catch (err) {
        // Backend error - show error state with retry option (NOT localStorage fallback)
        console.error("❌ Failed to load shortlisted products from backend:", err);
        setError("Failed to load shortlisted products. Please try again.");
        setItems([]);
        // Don't fall back to localStorage - show error instead
      } finally {
        setLoading(false);
      }
    };

    loadShortlistedProducts();
  }, [retryKey]); // Re-run when retryKey changes

  useEffect(() => {
    const loadBorrowerContact = async () => {
      if (!isAuthenticated()) {
        setBorrowerContact({ name: "", phone: "" });
        return;
      }
      const email = getBorrowerEmail();
      if (!email) return;
      try {
        const res = await getBorrowerProfile(email);
        const doc = res?.data || res || {};
        const name = [doc.first_name, doc.last_name].filter(Boolean).join(" ").trim();
        setBorrowerContact({
          name: name || String(doc.full_name || "").trim(),
          phone: doc.phone_number || doc.phone || "",
        });
      } catch {
        setBorrowerContact({ name: "", phone: "" });
      }
    };
    loadBorrowerContact();
  }, []);

  // Check which products are linked to ongoing applications
  useEffect(() => {
    const checkLinkedProducts = async () => {
      if (!isAuthenticated() || items.length === 0) {
        setLinkedProducts(new Map());
        return;
      }

      try {
        const userEmail = getBorrowerEmail();
        if (!userEmail) {
          setLinkedProducts(new Map());
          return;
        }

        console.log("🔍 [checkLinkedProducts] Checking for linked applications...");

        // Get all applications for the user
        const applications = await listApplications({ applicant: userEmail });

        if (!applications || !Array.isArray(applications)) {
          setLinkedProducts(new Map());
          return;
        }

        // Define ongoing statuses (anything except Completion or Cancelled)
        const ongoingStatuses = [
          "pre enquiry",
          "enquiry",
          "application",
          "approval",
          "valuation",
          "legal",
          "legals"
        ];

        const linkedMap = new Map();

        // Check each application
        applications.forEach((app) => {
          const status = String(app.status || "").trim().toLowerCase();

          // Skip completed or cancelled applications
          if (status.includes("completion") ||
            status.includes("complete") ||
            status.includes("cancelled") ||
            status.includes("withdrawn") ||
            status.includes("rejected")) {
            return;
          }

          const isOngoing = ongoingStatuses.some(s => status.includes(s));

          if (isOngoing && app.product) {
            const appProductId = String(app.product).trim().toLowerCase();

            // Store application info for displaying in UI
            const appInfo = {
              appId: app.name || app.id || "Unknown",
              appName: app.name || app.id || "Unknown",
              status: app.status || "Unknown",
              product: app.product,
              submittedOn: app.submitted_on || app.creation,
              currentStage: app.current_stage || app.stage || app.status
            };

            // Store by product ID
            linkedMap.set(app.product, appInfo);
            linkedMap.set(appProductId, appInfo);

            console.log(`🔗 [checkLinkedProducts] Linked: ${app.product} -> ${app.name} (${app.status})`);
          }
        });

        console.log(`🔍 [checkLinkedProducts] Found ${linkedMap.size} linked products`);
        setLinkedProducts(linkedMap);
      } catch (error) {
        console.error("❌ [checkLinkedProducts] Error:", error);
        setLinkedProducts(new Map());
      }
    };

    checkLinkedProducts();

    // Re-check every 15 seconds to catch status changes
    const interval = setInterval(() => {
      console.log("🔄 [checkLinkedProducts] Auto-refresh check");
      checkLinkedProducts();
    }, 15000);

    return () => clearInterval(interval);
  }, [items, retryKey]);

  // Save to localStorage ONLY for non-authenticated users (temporary storage)
  // Authenticated users should NOT use localStorage - backend is the source of truth
  // Note: For authenticated users, localStorage is cleared after sync on login
  // and should not be used again to avoid stale data
  useEffect(() => {
    if (!isAuthenticated() && items.length > 0) {
      safeWriteArray(LS_SHORTLIST, items.slice(0, 5));
    }
  }, [items]);

  // Category tab follows the sourcing page the user came from (BTL, IF, …)
  const [selectedCategory, setSelectedCategory] = useState(() => resolveShortlistCategory(location));
  // Sort: same options as sourcing, default Interest rate
  const [sortBy, setSortBy] = useState("rate");

  useEffect(() => {
    const next = resolveShortlistCategory(location);
    setSelectedCategory(next);
    rememberSourcingCategory(next);
  }, [location.search, location.state]);

  const selectCategoryTab = (categoryId) => {
    setSelectedCategory(categoryId);
    rememberSourcingCategory(categoryId);
    navigate(shortlistPathForCategory(categoryId), { replace: true, state: location.state });
  };

  // Available categories (only BTL enabled for now)
  const categories = [
    { id: "BTL", label: "BTL", enabled: true },
    { id: "IF", label: "Invoice Finance", enabled: true },
    { id: "ASSET", label: "Asset Finance", enabled: false },
    { id: "COMMERCIAL", label: "Commercial", enabled: false },
  ];

  // Comparators for shortlist (same logic as sourcing: low → high)
  const sortComparators = useMemo(() => ({
    cost: (a, b) => safeNum(a.totalCost) - safeNum(b.totalCost),
    fee: (a, b) => safeNum(a.arrangement_fee ?? a.fees?.arrangement) - safeNum(b.arrangement_fee ?? b.fees?.arrangement),
    rate: (a, b) =>
      selectedCategory === "IF"
        ? safeNum(a.apr ?? a.ratePct ?? a.rate) - safeNum(b.apr ?? b.ratePct ?? b.rate)
        : safeNum(a.rate) - safeNum(b.rate),
    repayment: (a, b) => safeNum(a.monthlyCost) - safeNum(b.monthlyCost),
  }), [selectedCategory]);

  const categoryCounts = useMemo(() => {
    const counts = { BTL: 0, IF: 0, ASSET: 0, COMMERCIAL: 0 };
    items.forEach((item) => {
      if (!isVisibleShortlistedProduct(item)) return;
      const cat = shortlistTabForCategory(normalizeProductType(item));
      if (counts[cat] != null) counts[cat] += 1;
    });
    return counts;
  }, [items]);

  // Filter and sort shortlist by selected category and sort option
  const shortlist = useMemo(() => {
    let filtered = items
      .filter((item) => {
        if (!isVisibleShortlistedProduct(item)) return false;
        const itemCategory = shortlistTabForCategory(normalizeProductType(item));
        return itemCategory === selectedCategory;
      })
      .slice();
    const cmp = sortComparators[sortBy] || sortComparators.rate;
    filtered.sort(cmp);
    return filtered.slice(0, 5);
  }, [items, selectedCategory, sortBy, sortComparators]);
  const soleProductId = shortlist.length === 1 ? shortlist[0].id : null;
  useEffect(() => {
    if (soleProductId && String(selectedId) !== String(soleProductId)) {
      setSelectedId(soleProductId);
    }
  }, [soleProductId, selectedId]);
  const shouldShowShortlistFullWarning =
    (shortlistFullWarning || items.length >= 5) && !dismissedShortlistFullWarning;
  useEffect(() => {
    if (items.length < 5) {
      setDismissedShortlistFullWarning(false);
      setShortlistFullWarning(false);
    }
  }, [items.length]);

  const close = () => navigate("/borrower/application");

  const askRemoveOne = (id) => {
    setConfirmDialog({
      open: true,
      type: "removeOne",
      productId: id,
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ open: false, type: null, productId: null });
  };

  const openCallbackDialog = () => {
    const name = String(borrowerContact.name || "").trim();
    const phone = String(borrowerContact.phone || "").trim();
    const hasSavedContact = Boolean(name) && isValidPhone(phone, { required: true });
    setCallbackDialog({
      open: true,
      name,
      phone,
      loading: hasSavedContact,
      error: "",
      done: false,
      skipForm: hasSavedContact,
    });
    if (hasSavedContact) {
      submitCallbackRequestFromShortlist({ name, phone, skipForm: true });
    }
  };

  const closeCallbackDialog = () => {
    if (callbackDialog.loading) return;
    setCallbackDialog({
      open: false,
      name: "",
      phone: "",
      loading: false,
      error: "",
      done: false,
      skipForm: false,
    });
  };

  const submitCallbackRequestFromShortlist = async (overrides = {}) => {
    const name = String(overrides.name ?? callbackDialog.name ?? "").trim();
    const phone = String(overrides.phone ?? callbackDialog.phone ?? "").trim();
    const skipForm = Boolean(overrides.skipForm || callbackDialog.skipForm);
    if (!name) {
      setCallbackDialog((prev) => ({ ...prev, error: "Please enter your name." }));
      return;
    }
    if (!isValidPhone(phone, { required: true })) {
      setCallbackDialog((prev) => ({ ...prev, error: "Please enter a valid mobile number." }));
      return;
    }
    setCallbackDialog((prev) => ({ ...prev, loading: true, error: "", skipForm }));
    try {
      await submitCallbackRequest({
        name,
        phone,
        email: getBorrowerEmail() || "",
        productIds: shortlistProductIds(shortlist),
        category: selectedCategory,
      });
      setBorrowerContact({ name, phone });
      setCallbackDialog((prev) => ({
        ...prev,
        open: true,
        name,
        phone,
        loading: false,
        error: "",
        done: true,
        skipForm,
      }));
    } catch (err) {
      setCallbackDialog((prev) => ({
        ...prev,
        loading: false,
        error: err?.message || "Could not send callback request. Please try again.",
        skipForm,
      }));
    }
  };

  const removeOne = async (id) => {
    const product = items.find((x) => String(x.id) === String(id));
    if (!product) return;

    const productIdentifiers = getProductIdentifiers(product);

    if (isAuthenticated()) {
      try {
        const userEmail = getBorrowerEmail();
        if (!userEmail) {
          alert("Unable to verify user authentication. Please try again.");
          return;
        }

        const applications = await listApplications({ applicant: userEmail });

        if (applications && Array.isArray(applications)) {
          const ongoingStatuses = [
            "pre enquiry",
            "enquiry",
            "application",
            "approval",
            "valuation",
            "legal",
            "legals"
          ];

          let linkedApp = null;

          for (const app of applications) {
            const status = String(app.status || "").trim().toLowerCase();

            if (status.includes("completion") ||
              status.includes("complete") ||
              status.includes("cancelled") ||
              status.includes("withdrawn") ||
              status.includes("rejected")) {
              continue;
            }

            const isOngoing = ongoingStatuses.some(
              ongoingStatus => status.includes(ongoingStatus)
            );

            if (!isOngoing) continue;

            const appProduct = app.product || app.product_id || app.product_name || "";
            const appProductId = String(appProduct).trim().toLowerCase();

            const isMatch = productIdentifiers.some(
              identifier => identifier === appProductId || appProductId.includes(identifier)
            );

            if (isMatch) {
              linkedApp = {
                appId: app.name || app.id || "Unknown",
                status: app.status || "Unknown",
                submittedOn: app.submitted_on || app.submitted_date || app.creation,
                currentStage: app.current_stage || app.stage || app.status
              };
              break;
            }
          }

          if (linkedApp) {
            setError(`Cannot delete: Product is linked to application ${linkedApp.appId} (${linkedApp.status}). Complete or cancel the application first.`);
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
          }
        }
      } catch (error) {
        const proceed = window.confirm(
          "Unable to verify if this product is linked to an application. Do you want to proceed with deletion anyway?"
        );
        if (!proceed) return;
      }
    }

    setLoadingId(id);

    const shortlistedProductName = product.shortlisted_doc_name ||
      product._raw?.name ||
      (normalizeProductType(product) === "IF" ? null : product.name) ||
      product.product_id ||
      product.mortgage_product;

    if (isAuthenticated() && shortlistedProductName) {
      try {
        const result = await deleteShortlistedProductByName(shortlistedProductName);

        if (result.success) {
          setItems((prev) => prev.filter((x) => String(x.id) !== String(id)));
          if (String(selectedId) === String(id)) setSelectedId(null);
          if (String(openDetailsFor) === String(id)) setOpenDetailsFor(null);

          setError(null);
          showToast("success", "Product removed from shortlist");
          setRetryKey((prev) => prev + 1);

          try {
            const confirmed = safeReadArray(LS_SHORTLIST).filter(
              (x) => !getProductIdentifiers(x).some((pid) => productIdentifiers.includes(pid))
            );
            const pending = safeReadArray(LS_PENDING).filter(
              (x) => !getProductIdentifiers(x).some((pid) => productIdentifiers.includes(pid))
            );
            safeWriteArray(LS_SHORTLIST, confirmed);
            safeWriteArray(LS_PENDING, pending);
          } catch (e) { /* ignore */ }
        } else {
          setError(result.error || "Failed to delete product. Please try again.");
          setRetryKey((prev) => prev + 1);
        }
      } catch (error) {
        setError(error.message || "Failed to delete product. Please try again.");
        setRetryKey((prev) => prev + 1);
      } finally {
        setLoadingId(null);
      }
    } else {
      setItems((prev) => prev.filter((x) => String(x.id) !== String(id)));
      if (String(selectedId) === String(id)) setSelectedId(null);
      if (String(openDetailsFor) === String(id)) setOpenDetailsFor(null);

      try {
        const confirmed = safeReadArray(LS_SHORTLIST).filter((x) => {
          const xIds = getProductIdentifiers(x);
          return !productIdentifiers.some((pid) => xIds.includes(pid));
        });
        const pending = safeReadArray(LS_PENDING).filter((x) => {
          const xIds = getProductIdentifiers(x);
          return !productIdentifiers.some((pid) => xIds.includes(pid));
        });
        safeWriteArray(LS_SHORTLIST, confirmed);
        safeWriteArray(LS_PENDING, pending);
        showToast("success", "Product removed from shortlist");
      } catch (e) {
        console.error("Failed to update localStorage:", e);
      } finally {
        setLoadingId(null);
      }
    }
  };

  const onConfirmDialogAction = async () => {
    if (confirmDialog.type === "removeOne" && confirmDialog.productId != null) {
      const id = confirmDialog.productId;
      setConfirmDialog({ open: false, type: null, productId: null });
      await removeOne(id);
    }
  };

  const applyNow = (product) => {
    const productId = String(product?.id || "");
    if (!productId) {
      window.alert("Invalid product.");
      return;
    }

    const isSoleProduct = soleProductId && String(soleProductId) === productId;
    if (!isSoleProduct && String(selectedId) !== String(productId)) {
      setError("Please select this product first before applying.");
      return;
    }

    setError(null);

    // Prepare intent with product information (no API call - application will be created on submit)
    const normalizedType = normalizeProductType(product);
    const intent = {
      productId,
      productType: normalizedType, // Pass full product to get category
      product: product, // Pass full product object for creating application later
      loanPurpose: product.loanPurpose || product.loan_purpose || "Purchase",
      ts: Date.now(),
    };

    console.log("Navigating to application form with intent:", intent);
    console.log("Product data:", product);
    console.log("Normalized product type:", normalizedType);

    try {
      sessionStorage.setItem(INTENT_KEY, JSON.stringify(intent));
    } catch (err) {
      console.error("Failed to save intent:", err);
    }

    // Navigate to application form
    navigate("/borrower/apply", { state: intent });
  };

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/40" onClick={close} aria-hidden />
      <div className="absolute inset-0 p-4 md:p-8 overflow-hidden">
        <div className="mx-auto h-full max-w-6xl rounded-2xl bg-white shadow-2xl border border-slate-200 flex flex-col">
          {/* Header */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between px-4 sm:px-5 py-4 border-b border-slate-200">
            <div className="min-w-0">
              <h2 className="text-lg sm:text-xl font-bold text-slate-900">Your shortlisted products</h2>
              <p className="text-xs text-slate-500">
                Apply online, or ask our team first. Free, no obligation.
              </p>
            </div>

            <div className="flex items-center gap-3 shrink-0">
              <button
                type="button"
                onClick={() => navigate(sourcingPathForCategory(selectedCategory))}
                className="inline-flex items-center gap-2.5 rounded-xl bg-sky-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm ring-1 ring-sky-500/30 hover:bg-sky-700 hover:shadow-md active:bg-sky-800 transition-all duration-200"
              >
                <Plus className="h-4 w-4 shrink-0" aria-hidden />
                <span>Add Products</span>
              </button>

              <button
                onClick={close}
                className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-slate-200 hover:bg-slate-50"
                aria-label="Close"
                title="Close"
              >
                <X className="w-5 h-5 text-slate-700" />
              </button>
            </div>
          </div>

          {/* Category Navigation Bar */}
          <div className="px-4 sm:px-5 py-3 border-b border-slate-200 bg-slate-50 overflow-x-auto">
            <div className="flex items-center gap-2 min-w-max sm:min-w-0">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => category.enabled && selectCategoryTab(category.id)}
                  disabled={!category.enabled}
                  className={`
                    inline-flex items-center px-4 py-2 rounded-lg text-sm font-semibold transition-all
                    ${selectedCategory === category.id
                      ? "bg-sky-600 text-white shadow-sm"
                      : category.enabled
                        ? "bg-white text-slate-700 border border-slate-300 hover:bg-slate-100 hover:border-slate-400"
                        : "bg-slate-100 text-slate-400 border border-slate-200 cursor-not-allowed opacity-60"
                    }
                  `}
                  title={!category.enabled ? "Coming soon" : `View ${category.label} products`}
                >
                  <span>{category.label}</span>
                  {category.enabled ? (
                    <span
                      className={`ml-2 inline-flex min-w-[1.15rem] h-5 items-center justify-center rounded-full px-1.5 text-[11px] font-bold ${
                        selectedCategory === category.id
                          ? "bg-white/20 text-white"
                          : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {categoryCounts[category.id] || 0}
                    </span>
                  ) : null}
                </button>
              ))}
            </div>
          </div>

          {shouldShowShortlistFullWarning && (
            <div className="mx-5 mt-4 flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800">
              <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600" />
              <p className="text-sm font-medium">
                Your shortlist is full (5 products). Remove one from Shortlisted products to add more from Sourcing.
              </p>
              <button
                type="button"
                className="ml-auto shrink-0 rounded-lg p-1.5 text-amber-600 hover:bg-amber-100"
                onClick={() => {
                  setShortlistFullWarning(false);
                  setDismissedShortlistFullWarning(true);
                  try {
                    sessionStorage.removeItem("ds_shortlist_full_warning");
                  } catch { /* ignore */ }
                }}
                aria-label="Dismiss"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          )}

          {toast && (
            <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-xl border shadow-lg flex items-center gap-2 ${
              toast.type === "success"
                ? "bg-green-50 border-green-200 text-green-800"
                : "bg-red-50 border-red-200 text-red-800"
            }`}>
              {toast.type === "success" ? (
                <Info className="h-5 w-5" />
              ) : (
                <AlertCircle className="h-5 w-5" />
              )}
              <span className="text-sm font-medium">{toast.message}</span>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mx-5 mt-4 p-4 rounded-xl bg-rose-50 border border-rose-200">
              <div className="text-sm font-medium text-rose-800 mb-3 whitespace-pre-line">{error}</div>
              <button
                onClick={() => {
                  setError(null);
                  setLoading(true);
                  // Trigger effect re-run by incrementing retryKey
                  setRetryKey(prev => prev + 1);
                }}
                className="text-sm font-semibold text-rose-700 hover:text-rose-900 underline"
              >
                Retry
              </button>
            </div>
          )}

          {/* Body */}
          <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-6 pt-4">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-20">
                <Loader2 className="h-12 w-12 animate-spin text-sky-500 mb-4" />
                <p className="text-slate-600">Loading shortlisted products...</p>
              </div>
            ) : !shortlist.length ? (
              <div className="mt-6">
                <EmptyState
                  label={`No ${categories.find(c => c.id === selectedCategory)?.label || selectedCategory} products found`}
                  hint={`Go back to Borrower Sourcing and shortlist ${categories.find(c => c.id === selectedCategory)?.label || selectedCategory} products.`}
                  onNavigateToSourcing={() => navigate(sourcingPathForCategory(selectedCategory))}
                />
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                <div className="flex justify-end">
                  <SortControl
                    sortBy={sortBy}
                    setSortBy={setSortBy}
                    options={selectedCategory === "IF" ? SORT_OPTIONS_IF : SORT_OPTIONS_BTL}
                  />
                </div>
                <div className="grid grid-cols-1 gap-5 md:gap-6">
                {shortlist.map((p) => {
                  const isSoleProduct = Boolean(soleProductId);
                  const isSelected =
                    isSoleProduct || (selectedId && String(selectedId) === String(p.id));
                  const isDisabled =
                    !isSoleProduct && selectedId && String(selectedId) !== String(p.id);

                  // Get all identifiers for this product
                  const productIdentifiers = getProductIdentifiers(p);

                  // Check if product is linked using any identifier
                  let isLinked = false;
                  let linkedAppInfo = null;

                  for (const identifier of productIdentifiers) {
                    if (linkedProducts.has(identifier)) {
                      isLinked = true;
                      linkedAppInfo = linkedProducts.get(identifier);
                      break;
                    }
                  }

                  // Also check case-insensitive matching
                  if (!isLinked) {
                    for (const [key, value] of linkedProducts.entries()) {
                      if (productIdentifiers.some(id => String(key).toLowerCase() === id)) {
                        isLinked = true;
                        linkedAppInfo = value;
                        break;
                      }
                    }
                  }

                  console.log(`🔍 [Render] Product ${p.id} linked status:`, {
                    isLinked,
                    productIdentifiers,
                    linkedKeys: Array.from(linkedProducts.keys()),
                    linkedAppInfo
                  });

                  return (
                    <ShortlistCard
                      key={p.id}
                      product={p}
                      onApply={() => applyNow(p)}
                      onRemove={() => askRemoveOne(p.id)}
                      disableApply={isDisabled}
                      isLoading={String(loadingId) === String(p.id)}
                      isOpen={openDetailsFor === p.id}
                      onToggle={() => setOpenDetailsFor((curr) => (curr === p.id ? null : p.id))}
                      isSelected={isSelected}
                      onSelect={() => setSelectedId(p.id)}
                      isLinkedToApplication={isLinked}
                      linkedAppInfo={linkedAppInfo}
                    />
                  );
                })}
                </div>
                <div className="relative w-full md:w-fit md:self-start max-w-full overflow-visible">
                  <div className="pointer-events-none absolute left-1.5 md:left-3 top-6 md:top-1/2 z-10 h-12 w-14 md:h-11 md:w-16 md:-translate-y-1/2">
                    <img
                      src="/Waving-Photoroom.png"
                      alt="Smiling polar bear waving"
                      className="callback-bear-float absolute left-1/2 top-1/2 h-28 w-28 md:h-[12.5rem] md:w-[12.5rem] max-w-none object-contain object-center select-none"
                      draggable={false}
                    />
                  </div>
                  <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-5 rounded-2xl border border-sky-100 bg-sky-50/70 py-3 pl-[4.25rem] pr-3 md:py-2.5 md:pl-24 md:pr-3">
                    <div className="min-w-0">
                      <h3 className="text-sm font-bold text-slate-900 leading-snug">Not sure about choosing?</h3>
                      <p className="mt-0.5 text-xs text-slate-600 leading-snug">
                        Our team can review your shortlist before you apply.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={openCallbackDialog}
                      className="w-full md:w-auto shrink-0 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-sky-600 px-4 py-2.5 md:py-2 text-sm font-semibold text-white shadow-sm hover:bg-sky-700 transition-colors"
                    >
                      <Phone className="h-4 w-4" />
                      Request a callback
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <ConfirmDialog
        open={confirmDialog.open}
        title="Remove this shortlisted product?"
        message="This product will be removed from your shortlist."
        confirmLabel="Remove"
        onConfirm={onConfirmDialogAction}
        onCancel={closeConfirmDialog}
        loading={Boolean(loadingId)}
      />
      <CallbackRequestDialog
        open={callbackDialog.open}
        name={callbackDialog.name}
        phone={callbackDialog.phone}
        productIds={shortlistProductIds(shortlist)}
        error={callbackDialog.error}
        loading={callbackDialog.loading}
        done={callbackDialog.done}
        skipForm={callbackDialog.skipForm}
        onNameChange={(value) => setCallbackDialog((prev) => ({ ...prev, name: value, error: "" }))}
        onPhoneChange={(value) => setCallbackDialog((prev) => ({ ...prev, phone: value, error: "" }))}
        onConfirm={() => submitCallbackRequestFromShortlist()}
        onCancel={closeCallbackDialog}
      />
    </div>
  );
}